import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField/TextField';
import Loader from 'components/Common/Loader/Loader';
import { useCustomer } from 'hooks/useCustomer';
import { getRoutesUri, getUri } from 'services/generic';
import { formatDecimalPrice, formatPrice } from 'services/formatter';
import { PersonalAccountDetails } from 'types/PersonalAccountDetails';
import styles from './AccountSummary.module.scss';

function AccountSummary(): JSX.Element {
  const [
    accountDetails,
    setAccountDetails,
  ] = useState<PersonalAccountDetails>();
  const [accountDetailsError, setAccountDetailsError] = useState(null);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState(false);
  const [userData, setUserData] = useState<UserData[]>([]);
  const { customer } = useCustomer();
  const [price] = useState(0);
  const { t } = useTranslation();
  const submitTrans = t('LABEL_CONTINUE_TO_PAYMENT');

  useEffect(() => {
    if (!customer?.email) {
      return;
    }

    fetch(`${process.env.REACT_APP_URL_API_PERSONAL_ACCOUNT}/init`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-AUTH-TOKEN': `${process.env.REACT_APP_API_SYMFONY_API_KEY}`,
      },
      body: JSON.stringify({
        email: customer?.email,
        password: '',
      }),
    })
      .then(res => res.json())
      .then(
        result => {
          setAccountDetails({ ...accountDetails, ...result });
        },
        error => {
          setAccountDetailsError(error);
        }
      );
  }, [customer]);

  useEffect(() => {
    if (!accountDetails || accountDetails.error?.length) {
      return;
    }

    setUserData([
      {
        label: t('LABEL_NAME'),
        value: accountDetails?.surname + ' ' + accountDetails?.forename,
      },
      { label: t('LABEL_ACCOUNT_NUMBER'), value: accountDetails?.account },
      {
        label: t('LABEL_ACCOUNT_BALANCE'),
        value: accountDetails?.creditBalance,
        bold: true,
      },
      {
        label: t('LABEL_CREDIT_LIMIT'),
        value: formatPrice(accountDetails?.creditLimit.toString()),
      },
      {
        label: t('LABEL_CREDIT_AVAILABLE'),
        value: formatPrice(accountDetails?.availableCredit.toString()),
      },
    ]);
  }, [accountDetails]);

  useEffect(() => {
    !amount ||
    (amount &&
      parseFloat(amount.replace(',', '.')) <= 1000 &&
      amount &&
      parseFloat(amount.replace(',', '.')) > 0)
      ? setAmountError(false)
      : setAmountError(true);
  }, [amount]);

  function handleChange(value) {
    if (!value || (value && !isNaN(Number(value.replace(',', '.'))))) {
      setAmount(formatDecimalPrice(value));
    }
  }

  function handleCancel(event) {
    event.preventDefault();
    window.location.href = getUri(`${process.env.REACT_APP_URL_CUSTOMER}`);
  }

  function handleSubmit(event) {
    event.preventDefault();
    window.location.href =
      process.env.REACT_APP_URL_API_SIPS +
      '/redirect?personalAccount=true&accountNumber=' +
      accountDetails?.account +
      '&amount=' +
      amount.replace(',', '.');
  }

  if (customer && !customer.isLoggedIn) {
    document.location.href = getRoutesUri('customerLogin') as string;
  }

  if (accountDetailsError || accountDetails?.error) {
    return (
      <div className={styles.container}>
        <div className={styles.sipsMessage + ' ' + styles.error}>
          {t('LABEL_ACCOUNT_LOAD_ERROR')}
        </div>
      </div>
    );
  }

  if (!accountDetails) {
    return (
      <div className={styles.container}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {price > 0 && (
        <div className={styles.errorMessage}>
          {t('LABEL_ACCOUNT_ERROR_MESSAGE', { price: price })}
        </div>
      )}
      <div>
        {userData.map(data => {
          const { label, value, bold } = data;
          return (
            <div className={styles.dataRow} key={label}>
              <div>{label}</div>
              <div className={bold ? styles.bold : ''}>{value}</div>
            </div>
          );
        })}
        <div className={styles.dataRow}>
          <form
            // action="https://afibel.local/uk/sips/process/redirect"
            method="POST"
            onSubmit={e => handleSubmit(e)}
          >
            <div className={styles.dataRow}>
              <div>{t('LABEL_AMOUNT_TO_PAY')}</div>
              <div className={styles.amountContainer}>
                <TextField
                  classes={{
                    root: styles.amountFieldRoot,
                  }}
                  error={amountError}
                  helperText={amountError ? t('LABEL_PRICE_NOT_ALLOWED') : ''}
                  id="outlined-basic"
                  //inputProps={{ pattern: '[0-9]*' }}
                  InputProps={{
                    classes: {
                      root: styles.amountInput,
                    },
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  name="amount"
                  onChange={e => handleChange(e.target.value)}
                  value={amount}
                  variant="outlined"
                />
                <p>{t('LABEL_PLEASE_CONFIRM')}</p>
                {/* <input type="hidden" name="personalAccount" value="true" />
                <input
                  type="hidden"
                  name="accountNumber"
                  value={accountDetails?.account}
                /> */}
              </div>
            </div>
            <div className={styles.dataRow}>
              <div>
                <button
                  className={styles.cancel}
                  onClick={e => handleCancel(e)}
                >
                  {t('LABEL_CANCEL')}
                </button>
              </div>
              <div>
                <input
                  className={styles.continue}
                  disabled={amountError || amount === ''}
                  type="submit"
                  value={submitTrans}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AccountSummary;

type UserData = {
  label: string;
  value: string;
  bold?: boolean;
};
